import { DateTime } from 'luxon';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { useEffect, useState, useCallback } from 'react';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { useRouteParams } from 'hooks/useRouteParams';
import PageLoader from 'components/PageLoader';
import { TestAttempt as TestAttemptData } from 'types/api/TestAttempt';
import { TestPreparation } from 'types/api/TestPreparation';
import { getTestAttemptById } from 'actions/testAttempts';
import Resources from 'components/TestAttempts/Resources';
import Instructions from 'components/TestAttempts/Instructions';
import Reliability from 'components/ReliabilityTest/ReliabilityTest';
import HonorCodeAgreement from 'components/TestAttempts/HonorCodeAgreement';
import KnowledgeTest from 'components/KnowledgeTest/KnowledgeTest';
import ExpiredTest from 'components/ReliabilityTest/ExpiredTest';
import ExpiredCalibration from 'components/Calibrations/ExpiredCalibration';
import { getTestPreparations } from 'actions/testPreparations';
import useCurrentUser from 'hooks/useCurrentUser';
import { parseToDateTime } from 'helpers/date/localTimezone';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from 'helpers/apiError';

type URLParams = {
  id: string;
};

type TestAttemptOrPreparation = TestAttemptData | TestPreparation;

function isTestPreparation(
  data: TestAttemptOrPreparation
): data is TestPreparation {
  return 'calibration_name' in data;
}

function TestAttempt({ testType }: { testType?: string }) {
  const { id } = useRouteParams<URLParams>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { data, run, isPending, isError, isSuccess } =
    useAsync<TestAttemptOrPreparation>();
  const [preTestState, setPreTestState] = useState(
    () => window.localStorage.getItem(`pre_test_state_${id}`) || 'resources'
  );
  const handlePreTestState = useCallback(
    (state: string) => {
      window.localStorage.setItem(`pre_test_state_${id}`, state);
      setPreTestState(state);
    },
    [setPreTestState, id]
  );

  useEffect(() => {
    const request =
      testType === 'calibration'
        ? getTestPreparations(Number(currentUser.id), Number(id))
        : getTestAttemptById(Number(id));

    request.catch(error => {
      if (error?.response?.status === 403) {
        const errorMessage = getApiErrorMessage(error, '');
        navigate('/forbidden', {
          state: errorMessage ? { errorMessage } : undefined
        });
      }
    });

    run(request);
  }, [run, id, testType, currentUser.id, navigate]);

  useEffect(() => {
    if (preTestState === 'resources') {
      handlePreTestState('resources');
    }
  }, [preTestState, handlePreTestState]);

  useDocumentTitle(
    isSuccess && data
      ? isTestPreparation(data)
        ? (data.calibration_name ?? data.coding_video.name)
        : data.title
      : undefined,
    isError
      ? i18n.ft(messages.unableToLoadTitle)
      : i18n.ft(messages.loadingTitle)
  );

  function isKnowledgeTest() {
    return (
      !!data &&
      !isTestPreparation(data) &&
      data.test_template.test_type === 'knowledge'
    );
  }

  if (isPending) {
    return <PageLoader />;
  }

  if (!data) {
    return null;
  }

  const expiredAtDt = parseToDateTime(data.expires_at);
  const renewableUntil = expiredAtDt?.plus({ days: 29 });
  const now = DateTime.utc();

  if (isTestPreparation(data)) {
    if (expiredAtDt && now > expiredAtDt) {
      return <ExpiredCalibration calibration_name={data.calibration_name} />;
    }

    return <KnowledgeTest id={parseInt(id)} testType={testType} />;
  }

  if (expiredAtDt && now > expiredAtDt) {
    if (renewableUntil && renewableUntil > now) {
      return <ExpiredTest {...data} renewable />;
    }
    return <ExpiredTest {...data} />;
  }

  if (isKnowledgeTest()) {
    return <KnowledgeTest id={parseInt(id)} testType={testType} />;
  }

  if (data.user_test.terms_accepted_at || preTestState === 'reliability_test') {
    return <Reliability id={id} />;
  }

  if (preTestState === 'resources') {
    return (
      <Resources
        title={data.title}
        testTemplateId={data.test_template.id as number}
        handlePreTestState={handlePreTestState}
        attemptId={id}
      />
    );
  }

  if (preTestState === 'instructions') {
    return (
      <Instructions
        title={data.title}
        handlePreTestState={handlePreTestState}
      />
    );
  }

  return (
    <HonorCodeAgreement
      title={data.title}
      userTestId={data.user_test.id.toString()}
      testAttemptId={parseInt(id)}
      handlePreTestState={handlePreTestState}
      testType={data.test_template.test_type}
    />
  );
}

export default TestAttempt;
